// Subdomain pattern detection. Maps patterns observed in
// `client_enrichment.ct_subdomains` (certificate-transparency-derived list)
// into operational-surface categories that shift threat profile weights.
//
// Pattern matching is ordered: more specific first, so 'patient-portal'
// matches before generic 'portal'. Each match contributes a category tag
// that the main overlay engine consumes.

const PATTERNS = [
  // Customer-facing portals (industry-specific, highest signal)
  { regex: /\b(patient|chart|mychart|ehr|clinic|appointment)\b/i, category: 'patient_portal',  industries: ['TP-HC'] },
  { regex: /\b(tenant|tenants|lease|resident|residents)\b/i,      category: 'tenant_portal',   industries: ['TP-RE'] },
  { regex: /\b(investor|investors|ir|investor-relations)\b/i,     category: 'investor_portal', industries: ['TP-FIN-BK', 'TP-FIN-INS', 'TP-RE'] },
  { regex: /\b(student|alumni|registrar|gradebook|powerschool|infinitecampus|canvas|blackboard)\b/i, category: 'student_portal', industries: ['TP-EDU'] },
  { regex: /\b(donor|donors|donate|giving|fundraising)\b/i,       category: 'donor_portal',    industries: ['TP-NP'] },
  { regex: /\b(broker|agent|agents|producer|producers)\b/i,       category: 'agent_portal',    industries: ['TP-FIN-INS', 'TP-RE'] },
  { regex: /\b(closing|escrow|title|settlement)\b/i,              category: 'closing_workflow',industries: ['TP-RE'] },

  // Generic customer / employee portals (all profiles)
  { regex: /\b(portal|customer|client|user-portal)\b/i,           category: 'customer_portal' },
  { regex: /\b(staff|employee|hr|workday|peoplesoft)\b/i,         category: 'employee_portal' },

  // Authentication / identity surface (drives Scattered Spider, Lapsus$ relevance)
  { regex: /\b(okta|sso|auth|login|signin|saml|oauth|adfs|entra)\b/i, category: 'sso_idp_surface' },

  // Remote access / VPN (drives ransomware initial access)
  { regex: /\b(vpn|citrix|anyconnect|fortinet|globalprotect|ivanti|netscaler|pulse)\b/i, category: 'vpn_appliance' },
  { regex: /\b(rdp|terminal|gateway|remote|teleport)\b/i,         category: 'remote_access' },

  // Email / collaboration
  { regex: /\b(mail|smtp|imap|owa|autodiscover|webmail|exchange)\b/i, category: 'email_surface' },
  { regex: /\b(sharepoint|teams|onedrive|share)\b/i,              category: 'collab_surface' },

  // File transfer / data movement (Cl0p targeting)
  { regex: /\b(files|transfer|moveit|goanywhere|cleo|sftp|ftp)\b/i, category: 'file_transfer' },

  // Dev / test / staging (universal high concern — often forgotten and exposed)
  { regex: /\b(dev|test|staging|stg|qa|uat|sandbox)\b/i,          category: 'dev_infrastructure' },
  { regex: /\b(api|graphql|webhook|callback)\b/i,                 category: 'public_api' },

  // Legacy / abandoned
  { regex: /\b(old|legacy|archive|deprecated|backup-site)\b/i,    category: 'legacy_infrastructure' },

  // OT / IoT (industrial, building management)
  { regex: /\b(scada|ot|ics|hmi|bms|building|hvac|access-control)\b/i, category: 'ot_iot_surface' },

  // Payment / commerce
  { regex: /\b(checkout|payment|payments|pay|pos|merchant)\b/i,   category: 'payment_surface' },

  // Source / build (Lapsus$, supply chain)
  { regex: /\b(github|gitlab|bitbucket|jenkins|ci|build|artifactory)\b/i, category: 'devops_surface' }
]

// Returns: { categories: Set<string>, matches: [{ subdomain, category, industries? }] }
export function detectSubdomainSignals(ctSubdomains = [], options = {}) {
  const { profileKey } = options
  const categories = new Set()
  const matches = []

  for (const sub of ctSubdomains) {
    if (!sub || typeof sub !== 'string') continue
    const lower = sub.toLowerCase()
    for (const p of PATTERNS) {
      if (p.regex.test(lower)) {
        // Skip industry-scoped categories that don't apply to this profile
        if (p.industries && profileKey && !p.industries.includes(profileKey)) continue
        categories.add(p.category)
        matches.push({ subdomain: sub, category: p.category, industries: p.industries })
        break
      }
    }
  }

  return { categories, matches, count: ctSubdomains.length }
}

// Translate categories into target-asset / attack-vector weight adjustments.
// Returns an additive adjustment object the overlay engine can compose.
export function subdomainSignalsToAdjustments(categories) {
  const adj = { targetAssets: {}, attackVectors: {}, threatActors: {} }

  if (categories.has('patient_portal'))   adj.targetAssets.patient_data = (adj.targetAssets.patient_data || 0) + 0.15
  if (categories.has('tenant_portal'))    adj.targetAssets.tenant_data = (adj.targetAssets.tenant_data || 0) + 0.15
  if (categories.has('investor_portal'))  adj.targetAssets.investor_data = (adj.targetAssets.investor_data || 0) + 0.20
  if (categories.has('student_portal'))   adj.targetAssets.student_data = (adj.targetAssets.student_data || 0) + 0.15
  if (categories.has('donor_portal'))     adj.targetAssets.donor_data = (adj.targetAssets.donor_data || 0) + 0.15

  // SSO/IdP surface elevates social-engineering and credential-theft vectors
  if (categories.has('sso_idp_surface')) {
    adj.attackVectors.social_engineering = (adj.attackVectors.social_engineering || 0) + 0.15
    adj.attackVectors.credential_theft = (adj.attackVectors.credential_theft || 0) + 0.10
  }

  // VPN / remote access elevates initial-access exploitation
  if (categories.has('vpn_appliance') || categories.has('remote_access')) {
    adj.attackVectors.unpatched_systems = (adj.attackVectors.unpatched_systems || 0) + 0.15
  }

  // File transfer surface elevates Cl0p-style supply-chain exploitation
  if (categories.has('file_transfer')) {
    adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.20
  }

  // Dev / legacy infrastructure exposed = significant attack surface concern
  if (categories.has('dev_infrastructure') || categories.has('legacy_infrastructure')) {
    adj.attackVectors.unpatched_systems = (adj.attackVectors.unpatched_systems || 0) + 0.10
  }

  // OT/IoT presence triggers ICS/OT attack vector concerns
  if (categories.has('ot_iot_surface')) {
    adj.attackVectors.ot_compromise = (adj.attackVectors.ot_compromise || 0) + 0.20
  }

  // Closing workflow exposure elevates BEC/wire-fraud vectors heavily
  if (categories.has('closing_workflow')) {
    adj.attackVectors.business_email_compromise = (adj.attackVectors.business_email_compromise || 0) + 0.25
  }

  // Payment surface elevates payment-card-data risks
  if (categories.has('payment_surface')) {
    adj.targetAssets.payment_data = (adj.targetAssets.payment_data || 0) + 0.15
  }

  // DevOps surface = source code and CI compromise — Lapsus$/state-actor concern
  if (categories.has('devops_surface')) {
    adj.attackVectors.supply_chain = (adj.attackVectors.supply_chain || 0) + 0.10
    adj.targetAssets.source_code = (adj.targetAssets.source_code || 0) + 0.15
  }

  return adj
}
